import { useEffect, useRef, useState } from 'react'
import BottomSheet from '../../design-system/patterns/BottomSheet.jsx'
import SegmentedControl from '../../design-system/patterns/SegmentedControl.jsx'
import { FilterChipToggle } from '../../design-system/patterns/FilterChip.jsx'
import Button from '../../design-system/primitives/Button.jsx'
import {
  buildDinnerRings,
  canSubmitPlan,
  cookNightCounts,
  cookNightWarning,
  dayShortLabel,
  dinnerAt,
  normalizeCookNights,
  scopeChoices,
} from '../../utils/planWithCookPreview.js'

const BIAS_OPTIONS = [
  { id: 'familiar', label: 'Familiar' },
  { id: 'mix', label: 'Mix' },
  { id: 'new', label: 'New' },
]

function captainLine(scope) {
  if (scope === 'next_week') {
    return "I'll put a few dinners on next week, from the recipes you already have."
  }
  return "I'll put a few dinners on the open nights, from the recipes you already have."
}

function timeLabel(recipe) {
  const time = typeof recipe?.time === 'string' ? recipe.time.trim() : ''
  if (!time || time === '—' || time === '-') return ''
  return time
}

function shopLabel(recipe) {
  const missing = Number(recipe?.missingTotal)
  if (!Number.isFinite(missing) || missing <= 0) return ''
  return missing === 1 ? '1 to shop' : `${missing} to shop`
}

/**
 * Plan with Cook overlay. Desktop docks beside the week like Add and Swap.
 * Mobile uses the shared bottom sheet. Plan it closes the panel immediately.
 */
export default function PlanWithCookSheet({
  open,
  initialDraft,
  openDayKeys = [],
  previewRecipes = [],
  density = 'desktop',
  onboardingMode = false,
  onClose,
  onSubmit,
  submitLabel = null,
}) {
  const [draft, setDraft] = useState(initialDraft)
  const [moreOpen, setMoreOpen] = useState(false)
  const [offsets, setOffsets] = useState([])
  const titleRef = useRef(null)
  const seedRef = useRef(initialDraft)
  seedRef.current = initialDraft

  useEffect(() => {
    if (!open) return undefined
    setDraft(seedRef.current)
    setMoreOpen(false)
    setOffsets([])
    const frame = requestAnimationFrame(() => titleRef.current?.focus())
    return () => cancelAnimationFrame(frame)
  }, [open])

  if (!open || !draft) return null

  const choices = scopeChoices(draft, openDayKeys)
  const scope = choices.some((choice) => choice.id === draft.scope) ? draft.scope : choices[0]?.id
  const scopedDraft = scope === draft.scope ? draft : { ...draft, scope }
  const active = {
    ...scopedDraft,
    cookNights: normalizeCookNights(scopedDraft, openDayKeys),
  }
  const cookOptions = cookNightCounts(active, openDayKeys)
    .map((count) => ({ id: String(count), label: String(count) }))
  const warning = cookNightWarning(active, openDayKeys)
  const ready = canSubmitPlan(active, openDayKeys)
  const rings = buildDinnerRings(previewRecipes, active.cookNights)
  const dinners = rings.map((ring, index) => dinnerAt(ring, offsets[index] || 0)).filter(Boolean)

  function update(patch) {
    setDraft((current) => ({ ...current, ...patch }))
  }

  function toggleDay(dayKey) {
    setDraft((current) => {
      const days = current.days || []
      const next = days.includes(dayKey) ? days.filter((key) => key !== dayKey) : [...days, dayKey]
      return { ...current, days: next }
    })
  }

  function cycleDinner(index) {
    const ring = rings[index] || []
    if (ring.length < 2) return
    const showing = new Set(
      dinners
        .filter((_, dinnerIndex) => dinnerIndex !== index)
        .map((recipe) => String(recipe.recipeId)),
    )
    setOffsets((current) => {
      let step = (current[index] || 0) + 1
      let guard = 0
      while (guard < ring.length) {
        const candidate = dinnerAt(ring, step)
        if (candidate && !showing.has(String(candidate.recipeId))) break
        step += 1
        guard += 1
      }
      if (guard >= ring.length) return current
      const next = current.slice()
      next[index] = step
      return next
    })
  }

  function submit() {
    if (!ready) return
    onSubmit?.({
      ...active,
      chosenRecipeIds: dinners.map((recipe) => recipe.recipeId),
    })
  }

  const body = (
    <div className="pwc-sheet">
      <div className="swap-recipe-sheet-header">
        <h2 className="swap-recipe-sheet-title" tabIndex={-1} ref={titleRef}>
          Plan your dinners
        </h2>
        {density !== 'mobile' && (
          <button type="button" className="swap-recipe-sheet-close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        )}
      </div>
      <div className="pwc-body">
        {active.hasUsualWeek ? <p className="pwc-hint">Starting from your usual week.</p> : null}

        <div className="pwc-captain">
          <span className="pwc-pot" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path
                fill="currentColor"
                d="M6.2 9.2h11.6c.3 0 .6.2.7.5l1.2 6.2a3.2 3.2 0 0 1-3.1 3.8H7.4a3.2 3.2 0 0 1-3.1-3.8l1.2-6.2c.1-.3.4-.5.7-.5Zm1.2 1.6-.8 4.2a1.6 1.6 0 0 0 1.6 1.9h7.6a1.6 1.6 0 0 0 1.6-1.9l-.8-4.2H7.4ZM8 7.2a1 1 0 0 1 1-1h.2a3.2 3.2 0 0 1 5.6 0H15a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Z"
              />
            </svg>
          </span>
          <p>{captainLine(scope)}</p>
        </div>

        {dinners.length > 0 ? (
          <div className="pwc-stack">
            {dinners.map((recipe, index) => {
              const time = timeLabel(recipe)
              const shop = shopLabel(recipe)
              const canChange = (rings[index] || []).length > 1
              return (
                <div className="pwc-night" key={`${index}-${recipe.recipeId}`}>
                  <div className="pwc-card">
                    <p className="pwc-recipe-name">{recipe.name}</p>
                    <div className="pwc-card-foot">
                      <p className="pwc-meta">
                        {time ? <span>{time}</span> : null}
                        {time && shop ? <span aria-hidden="true"> · </span> : null}
                        {shop ? <span className="pwc-shop">{shop}</span> : null}
                      </p>
                      {canChange ? (
                        <button
                          type="button"
                          className="pwc-change"
                          aria-label={`Try another dinner instead of ${recipe.name}`}
                          onClick={() => cycleDinner(index)}
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                            <path
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M7 7h9l-2.2-2.2M17 17H8l2.2 2.2"
                            />
                          </svg>
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : null}

        {!onboardingMode ? (
          <div className="pwc-field">
            <p className="pwc-label" id="pwc-scope-label">Which nights</p>
            <SegmentedControl
              ariaLabel="Which nights"
              options={choices}
              value={scope}
              onChange={(id) => {
                setOffsets([])
                update({ scope: id })
              }}
            />
          </div>
        ) : null}

        {!onboardingMode && scope === 'pick_nights' ? (
          <div className="pwc-chips" role="group" aria-label="Nights to plan">
            {openDayKeys.map((dayKey) => (
              <FilterChipToggle
                key={dayKey}
                label={dayShortLabel(dayKey)}
                active={(active.days || []).includes(dayKey)}
                aria-pressed={(active.days || []).includes(dayKey)}
                onClick={() => toggleDay(dayKey)}
              />
            ))}
          </div>
        ) : null}

        <div className="pwc-field">
          <p className="pwc-label" id="pwc-cook-label">Cook nights</p>
          <SegmentedControl
            ariaLabel="Cook nights"
            options={cookOptions}
            value={String(active.cookNights)}
            onChange={(id) => {
              setOffsets([])
              update({ cookNights: Number(id) })
            }}
          />
        </div>

        <button
          type="button"
          className="ghost pwc-more"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen((openNow) => !openNow)}
        >
          More options
        </button>
        {moreOpen ? (
          <SegmentedControl
            ariaLabel="Recipe mix"
            options={BIAS_OPTIONS}
            value={active.bias || 'mix'}
            onChange={(id) => update({ bias: id })}
          />
        ) : null}

        {warning ? <p className="pwc-warning">{warning}</p> : null}

        <div className="planner-panel-footer">
          <Button variant="primary" onClick={submit} disabled={!ready}>
            {submitLabel || (active.replaceExisting ? 'Update' : 'Plan it')}
          </Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  )

  if (density === 'mobile') {
    return (
      <BottomSheet open={open} onClose={onClose} aria-label="Plan your dinners">
        {body}
      </BottomSheet>
    )
  }

  return (
    <div className="swap-recipe-scrim swap-recipe-scrim--docked" role="presentation" onClick={onClose}>
      <div
        className="swap-recipe-panel"
        role="dialog"
        aria-label="Plan your dinners"
        onClick={(event) => event.stopPropagation()}
      >
        {body}
      </div>
    </div>
  )
}
